import {
  endOfDay,
  endOfMonth,
  endOfWeek,
  endOfYear,
  format,
  startOfDay,
  startOfMonth,
  startOfWeek,
  startOfYear,
  subDays,
  subMonths,
  subWeeks,
} from "date-fns";
import { useEffect, useState } from "react";
import { clampDateString, clampTimeString } from "../../../utils/functions";
import { Input } from "../../Input";
import type { DateSelected } from "..";
import * as styles from "./styles.css";

interface CalendarInputsProps {
  selectedDate: DateSelected;
  clearHover: () => void;
  setSelectedDate: (date: DateSelected) => void;
  disabled?: boolean;
}

const INPUT_HEIGHT = 28;
const DATE_WIDTH = 105;
const TIME_WIDTH = 60;

export const CalendarInputs = ({
  selectedDate,
  clearHover,
  setSelectedDate,
  disabled,
}: CalendarInputsProps) => {
  return (
    <div className={styles.inputsArea}>
      <div className={styles.dateHourInput}>
        <span>De</span>
        <DateInput
          value={selectedDate.start}
          type="start"
          compareDate={selectedDate.end}
          setValue={(e) => {
            clearHover();
            setSelectedDate({
              start: e,
              end: selectedDate.end,
            });
          }}
        />
        <TimeInput
          value={selectedDate.start}
          type="start"
          disabled={disabled}
          compareDate={selectedDate.end}
          setValue={(e) => {
            clearHover();
            setSelectedDate({
              start: e,
              end: selectedDate.end,
            });
          }}
        />
      </div>

      <div className={styles.dateHourInput}>
        <span>Até</span>
        <DateInput
          value={selectedDate.end}
          type="end"
          compareDate={selectedDate.start}
          setValue={(e) => {
            clearHover();
            setSelectedDate({
              start: selectedDate.start,
              end: e,
            });
          }}
        />
        <TimeInput
          value={selectedDate.end}
          type="end"
          disabled={disabled}
          compareDate={selectedDate.start}
          setValue={(e) => {
            clearHover();
            setSelectedDate({
              start: selectedDate.start,
              end: e,
            });
          }}
        />
      </div>
    </div>
  );
};

export const OPTIONS_FUNCTIONS = {
  Hoje: () => ({
    start: startOfDay(new Date()),
    end: endOfDay(new Date()),
  }),
  Ontem: () => {
    const yesterday = subDays(new Date(), 1);
    return {
      start: startOfDay(yesterday),
      end: endOfDay(yesterday),
    };
  },
  "Essa semana": () => ({
    start: startOfWeek(new Date()),
    end: endOfWeek(new Date()),
  }),
  "Semana passada": () => {
    const lastWeek = subWeeks(new Date(), 1);
    return {
      start: startOfWeek(lastWeek),
      end: endOfWeek(lastWeek),
    };
  },
  "Esse mês": () => ({
    start: startOfMonth(new Date()),
    end: endOfMonth(new Date()),
  }),
  "Mês passado": () => {
    const lastMonth = subMonths(new Date(), 1);
    return {
      start: startOfMonth(lastMonth),
      end: endOfMonth(lastMonth),
    };
  },
  "Esse ano": () => ({
    start: startOfYear(new Date()),
    end: endOfYear(new Date()),
  }),
};

interface CalendarOptionsProps {
  hideOptions: Array<keyof typeof OPTIONS_FUNCTIONS>;
  setSelectedDate: (date: DateSelected) => void;
  handleSelectDate: (date: DateSelected) => void;
}

export const CalendarOptions = ({
  hideOptions,
  setSelectedDate,
  handleSelectDate,
}: CalendarOptionsProps) => {
  return (
    <ul className={styles.optionsList}>
      {Object.keys(OPTIONS_FUNCTIONS)
        .filter(
          (option) =>
            !hideOptions.includes(option as keyof typeof OPTIONS_FUNCTIONS),
        )
        .map((option) => {
          return (
            <li
              key={option}
              onClick={() => {
                // setHoveredDays([]);
                setSelectedDate(
                  OPTIONS_FUNCTIONS[option as keyof typeof OPTIONS_FUNCTIONS](),
                );
                handleSelectDate(
                  OPTIONS_FUNCTIONS[option as keyof typeof OPTIONS_FUNCTIONS](),
                );
              }}
            >
              {option}
            </li>
          );
        })}
    </ul>
  );
};

interface TimeInputProps {
  value: Date;
  setValue: (value: Date) => void;
  type?: "start" | "end";
  compareDate?: Date;
  disabled?: boolean;
  styled?: React.CSSProperties;
}

export const TimeInput = ({
  value,
  setValue,
  type = "start",
  compareDate,
  disabled,
  styled,
}: TimeInputProps) => {
  const [timeValue, setTimeValue] = useState(format(value, "HH:mm"));

  useEffect(() => {
    setTimeValue(format(value, "HH:mm"));
  }, [value]);

  const handleBlur = () => {
    const clampedDate = clampTimeString(timeValue, value, type, compareDate);
    if (Number.isNaN(clampedDate.getTime())) {
      setTimeValue(format(value, "HH:mm"));
      return;
    }

    setValue(clampedDate);
  };

  return (
    <Input
      mask="99:99"
      value={timeValue}
      disabled={disabled}
      onFocus={() => {
        setTimeValue("");
      }}
      onChange={(e) => {
        setTimeValue(e.target.value);
      }}
      onBlur={handleBlur}
      size="small"
      style={{
        width: TIME_WIDTH,
        height: INPUT_HEIGHT,
        textAlign: "center",
        ...styled,
      }}
    />
  );
};

export const DateInput = ({
  value,
  setValue,
  type = "start",
  compareDate,
}: TimeInputProps) => {
  const [dateValue, setDateValue] = useState(
    format(new Date(value), "dd/MM/yyyy"),
  );

  useEffect(() => {
    setDateValue(format(value, "dd/MM/yyyy"));
  }, [value]);

  const handleBlur = () => {
    const clampedDate = clampDateString(dateValue, value, type, compareDate);

    if (Number.isNaN(clampedDate.getTime())) {
      setDateValue(format(value, "dd/MM/yyyy"));
      return;
    }

    setValue(clampedDate);
  };

  return (
    <Input
      mask="99/99/9999"
      value={dateValue}
      onFocus={() => {
        setDateValue("");
      }}
      onBlur={handleBlur}
      onChange={(e) => {
        setDateValue(e.target.value);
      }}
      size="small"
      style={{
        width: DATE_WIDTH,
        height: INPUT_HEIGHT,
        textAlign: "center",
      }}
    />
  );
};
